import { Group, Mesh, Material, Quaternion, Vector3, MeshBasicMaterial, Vector2, ExtrudeGeometry, PlaneGeometry, DoubleSide, BufferGeometry } from "three";
import { TextGeometry } from 'three/addons/geometries/TextGeometry.js';
import { SVGLoader } from 'three/addons/loaders/SVGLoader.js';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import FontProvider from "../../logic/FontProvider";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { createRoundedRectShape } from "../../utils/shapeUtils";
import IRoomNoteTarget, { IRoomLabelHoverTarget, ROOM_LABEL_HOVER_TARGET_KEY, ROOM_NOTE_TARGET_KEY } from "../../interfaces/IRoomNoteTarget";

const backgroundBoxPadding = new Vector2(sceneUnitsFromMm(100), sceneUnitsFromMm(100)); // Padding around text in background box
const NOTE_ICON_COLOR = "#555555";
const ADD_NOTE_ICON_COLOR = "#9a9a9a";  // Faint icon revealed on hover of a label without a note
const NOTE_ICON_GAP_RATIO = 0.4;        // Gap between text and icon, relative to font size
const NOTE_ICON_HIT_MARGIN_RATIO = 0.5; // Extra clickable margin around the icon, relative to font size
const NOTE_ICON_STROKE_WIDTH = 30;      // In SVG units. note-tool.svg uses 20, which reads too thin next to label text

// The AddTextAnnotation toolbar icon (public/icons/note-tool.svg) without its pin (the circle and the line below it)
const NOTE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
  <path d="M 126,146 H 406 a40,40 0 0 1 40,40 V 316 L 306,456 H 126 a40,40 0 0 1 -40,-40 V 186 a40,40 0 0 1 40,-40 Z" fill="none" stroke="#000000"/>
  <path d="M 306,456 V 356 a40,40 0 0 1 40,-40 h 100" fill="none" stroke="#000000"/>
  <path d="M 126,266 H 406" fill="none" stroke="#000000"/>
  <path d="M 126,326 H 275" fill="none" stroke="#000000"/>
  <path d="M 196,386 H 275" fill="none" stroke="#000000"/>
</svg>`;

let noteIconUnitGeometry: BufferGeometry | null = null;

// Stroke geometry of the note icon, centred on origin with height 1. Parsed once and cloned per label.
const getNoteIconUnitGeometry = (): BufferGeometry => {
    if (noteIconUnitGeometry) return noteIconUnitGeometry;

    const strokeStyle = SVGLoader.getStrokeStyle(NOTE_ICON_STROKE_WIDTH, "#000000", "miter", "butt", 4);
    const strokes = new SVGLoader().parse(NOTE_ICON_SVG).paths
        .flatMap(path => path.subPaths)
        .map(subPath => SVGLoader.pointsToStroke(subPath.getPoints(), strokeStyle));

    const geometry = BufferGeometryUtils.mergeGeometries(strokes)!;
    strokes.forEach(s => s.dispose());

    geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    const center = box.getCenter(new Vector3());
    const height = box.max.y - box.min.y;
    geometry.translate(-center.x, -center.y, 0);
    geometry.scale(1 / height, -1 / height, 1); // SVG y axis points down. Flips the winding too, so the material must be double sided
    geometry.computeBoundingBox();

    noteIconUnitGeometry = geometry;
    return geometry;
};

// Width of the note icon relative to its height
const getNoteIconAspect = (): number => {
    const box = getNoteIconUnitGeometry().boundingBox!;
    return box.max.x - box.min.x;
};
    
export default class NameLabelGeometry implements IRoomLabelHoverTarget {
    
    private text: string;
    private borderRadius: number;
    private backgroundColor: string;
    private textColor: string;
    private fontSize: number;
    private rotationOffset: number;
    private boundingBox: { min: Vector3; max: Vector3 } | null;
    private group: Group;
    private textMesh: Mesh | null = null;
    private backgroundMesh: Mesh | null = null;
    private noteIconMesh: Mesh | null = null;
    private noteIconHitMesh: Mesh | null = null;

    private textSize: Vector3;
    private renderOrder: number;
    private layer: number;
    private alwaysOnTop: boolean;
    private globalId: string;
    private note: string | null = null;
    private canAddNote: boolean = false;
    private isHovered: boolean = false;
    
    constructor(
        text: string = "Default Text",
        position: Vector3,
        renderOrder: number,
        layer: number,
        options?: {
            alwaysOnTop?: boolean;
            borderRadius?: number;
            backgroundColor?: string;
            textColor?: string;
            fontSize?: number;
            note?: string | null;   // Shows a clickable note icon after the text when set
            canAddNote?: boolean;   // Without a note, hovering the label reveals a clickable add-note icon
            globalId?: string;      // Identifies the room to the note dialog
        }
    ) {
        this.text = text;
        this.borderRadius = options?.borderRadius ?? sceneUnitsFromMm(50);
        this.backgroundColor = options?.backgroundColor ?? "white";
        this.textColor = options?.textColor ?? "darkslategray";
        this.fontSize = options?.fontSize ?? 0.15
        this.rotationOffset = 0;
        this.boundingBox = null;
        this.renderOrder = renderOrder;
        this.layer = layer;
        this.alwaysOnTop = options?.alwaysOnTop ?? true;
        this.globalId = options?.globalId ?? "";
        
        this.group = new Group();
        this.group.name = "NameLabel: " + text;
        this.group.position.copy(position);
        this.group.renderOrder = renderOrder;
        this.group.layers.set(layer);
        this.group.userData[ROOM_LABEL_HOVER_TARGET_KEY] = this;
        
        const textMaterial = new MeshBasicMaterial({    color: this.textColor, 
                                                        depthTest: !this.alwaysOnTop, 
                                                        opacity: 1.0, 
                                                        transparent: true   // Note, in order for text to be shown on top of background box, 
                                                                            // text must be transparent too, even if its opacity is 1.0
                                                    }); 
        const textGeom = new TextGeometry(this.text, 
            { 
                font: FontProvider.getInstanceSync().arial!,
                size: this.fontSize,
                curveSegments: 2,
                bevelEnabled: false,
                depth: sceneUnitsFromMm(10)
            });
            
        // Background box is sized from the text size
        textGeom.computeBoundingBox();
        this.textSize = textGeom.boundingBox ? textGeom.boundingBox.getSize(new Vector3()) : new Vector3(1,1,1);

        // Text mesh little bit above the box
        this.textMesh  = new Mesh(textGeom, textMaterial);
        this.textMesh.name = "Text mesh"
        this.textMesh.renderOrder = renderOrder + 1;
        this.textMesh.layers.set(layer);
        this.group.add(this.textMesh);

        this.note = options?.note?.trim() ? options.note : null;
        this.canAddNote = options?.canAddNote ?? false;
        const backgroundBoxSizeWithPadding = this.buildBackgroundAndNoteIcon();

        const translateV = new Vector3(
            backgroundBoxSizeWithPadding.x,
            backgroundBoxSizeWithPadding.y,
            0
        );

        this.group.position.copy(translateV);

        /* this.group.position.y -= backgroundBoxSizeWithPadding.y;
        
        this.group.matrix.decompose(    new Vector3(), 
                                        new Quaternion(), 
                                        new Vector3()); */

        //this.group.updateMatrixWorld(true);

        this.initialize();
    }

    /**
     * Rebuilds the label for a changed note, or a changed right to add one.
     * The background widens or narrows, so the text moves to stay centred with the icon.
     */
    public setNote(note: string | null, canAddNote: boolean): void {
        const normalizedNote = note?.trim() ? note : null;
        if (normalizedNote === this.note && canAddNote === this.canAddNote) return;

        this.note = normalizedNote;
        this.canAddNote = canAddNote;
        this.buildBackgroundAndNoteIcon();
    }

    public setHovered(hovered: boolean): void {
        this.isHovered = hovered;
        this.updateNoteIconVisibility();
    }

    // Lays out text and the optional note icon side by side, centred together. Returns the background box size.
    private buildBackgroundAndNoteIcon(): Vector3 {
        this.disposeBackgroundAndNoteIcon();

        // The icon slot exists when there is a note to show, or a note can be added
        const hasIconSlot = this.note !== null || this.canAddNote;
        const noteIconSize = this.textSize.y;
        const noteIconWidth = hasIconSlot ? noteIconSize * getNoteIconAspect() : 0;
        const noteSpace = hasIconSlot ? noteIconWidth + this.fontSize * NOTE_ICON_GAP_RATIO : 0;
        const contentWidth = this.textSize.x + noteSpace;

        const backgroundBoxSizeWithPadding = new Vector3(   contentWidth + backgroundBoxPadding.x,
                                                            this.textSize.y + backgroundBoxPadding.y,
                                                            this.textSize.z);
        
        const backgroundMaterial = new MeshBasicMaterial({ color: this.backgroundColor, depthTest: !this.alwaysOnTop, opacity: 0.7, transparent: true });

        const roundedRectShape = createRoundedRectShape(backgroundBoxSizeWithPadding.x, backgroundBoxSizeWithPadding.y, this.borderRadius);
        const backgroundGeometry = new ExtrudeGeometry(roundedRectShape, { depth: sceneUnitsFromMm(5), bevelEnabled: false });

        this.textMesh!.position.set(    -contentWidth / 2, 
                                        -this.textSize.y / 2, 
                                        sceneUnitsFromMm(2.5));

        this.backgroundMesh = new Mesh(backgroundGeometry, backgroundMaterial);
        this.backgroundMesh.name = "Background mesh";
        this.backgroundMesh.position.set(0,0,0); // Position box behind text
        this.backgroundMesh.renderOrder = this.renderOrder;
        this.backgroundMesh.layers.set(this.layer);
        this.group.add(this.backgroundMesh);

        if (hasIconSlot) {
            const iconCenterX = contentWidth / 2 - noteIconWidth / 2;
            this.addNoteIcon(noteIconSize, new Vector3(iconCenterX, 0, sceneUnitsFromMm(2.5)));
        }

        return backgroundBoxSizeWithPadding;
    }

    private addNoteIcon(size: number, center: Vector3): void {
        const iconColor = this.note ? NOTE_ICON_COLOR : ADD_NOTE_ICON_COLOR;
        const iconMaterial = new MeshBasicMaterial({ color: iconColor, depthTest: !this.alwaysOnTop, transparent: true, side: DoubleSide });
        this.noteIconMesh = new Mesh(getNoteIconUnitGeometry().clone().scale(size, size, 1), iconMaterial);
        this.noteIconMesh.name = "Note icon mesh";
        this.noteIconMesh.position.copy(center);
        this.noteIconMesh.renderOrder = this.renderOrder + 1;
        this.noteIconMesh.layers.set(this.layer);

        // Enlarged invisible hit box: must stay visible (transparent material) to pass the raycast visibility filter
        const hitMargin = this.fontSize * NOTE_ICON_HIT_MARGIN_RATIO * 2;
        const hitMaterial = new MeshBasicMaterial({ transparent: true, opacity: 0, depthTest: false, depthWrite: false, side: DoubleSide });
        this.noteIconHitMesh = new Mesh(new PlaneGeometry(size * getNoteIconAspect() + hitMargin, size + hitMargin), hitMaterial);
        this.noteIconHitMesh.name = "Note icon hit box";
        this.noteIconHitMesh.position.copy(center);
        this.noteIconHitMesh.layers.set(this.layer);
        const target: IRoomNoteTarget = { globalId: this.globalId, roomName: this.text, note: this.note };
        this.noteIconHitMesh.userData[ROOM_NOTE_TARGET_KEY] = target;

        this.group.add(this.noteIconMesh);
        this.group.add(this.noteIconHitMesh);
        this.updateNoteIconVisibility();
    }

    // A note's icon always shows. The add-note icon only shows while the label is hovered, its hit box stays clickable.
    private updateNoteIconVisibility(): void {
        if (this.noteIconMesh) {
            this.noteIconMesh.visible = this.note !== null || this.isHovered;
        }
    }

    private disposeBackgroundAndNoteIcon(): void {
        for (const mesh of [this.backgroundMesh, this.noteIconMesh, this.noteIconHitMesh]) {
            if (mesh) {
                this.group.remove(mesh);
                mesh.geometry.dispose();
                (mesh.material as Material).dispose();
            }
        }
        this.backgroundMesh = null;
        this.noteIconMesh = null;
        this.noteIconHitMesh = null;
    }

    private initialize(): void {
        //this.getBaseQuaternion();
    }

    private getBaseQuaternion(): void {
        if (!this.group.parent) return;
        
        this.group.updateMatrixWorld(true);
        const parentWorldQuat = new Quaternion();
        this.group.parent.getWorldQuaternion(parentWorldQuat);
        
        
        const inverseParentRotation = parentWorldQuat.clone().invert();
        this.group.setRotationFromQuaternion(inverseParentRotation);
    }

    setPosition(position: Vector3) {
        this.group.position.copy(position);
    }

    public setRotationOffset(offset: number): void {
        this.rotationOffset = offset;
        this.getBaseQuaternion();
        
        this.group.updateMatrixWorld(true);
        this.group.rotateZ(this.rotationOffset);
    }

    public setBoundingBox(boundingBox: { min: Vector3; max: Vector3 } | null): void {
        this.boundingBox = boundingBox;
        if (this.boundingBox) {
            this.group.position.setComponent(2, this.boundingBox.max.z);
        }
    }

    public getGroup(): Group {
        return this.group;
    }

    public setText(text: string): void {
        this.text = text;
    }

    public dispose(): void {
        if (this.textMesh) {
            this.textMesh.geometry.dispose();
            (this.textMesh.material as Material).dispose();
        }
        this.disposeBackgroundAndNoteIcon();
    }

}